import { environment } from '@src/environments/environment';
import { DatePipe } from "@angular/common";
import { NgForm } from '@angular/forms';
import { Component, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { TranslateService } from '@ngx-translate/core';
import { SessionService } from "@app/shared/services/session.service";
import { DialogService } from 'primeng/dynamicdialog';
import { ChangePasswordFormComponent } from '@src/app/shared/components/change-password-form/change-password-form.component';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { UsersService } from '@src/app/shared/backend/clients';
import { firstValueFrom } from 'rxjs';
import { AssociateTokenRequest, MfaService, AuthorisationService, NewPasswordChallengeRequest } from '@src/app/shared/backend/mfa';
import { LandingPage } from '@src/app/shared/backend/clients/model/landingPage';
import { Features } from '@src/app/shared/backend/auth/Feature';
import { LoginNavigationService } from '@src/app/shared/services/loginNavigation.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    host: {
        class: 'app-login'
    },
    providers: [DatePipe, DialogService],
    standalone: false
})
export class LoginComponent implements OnInit {

  public showPassword: boolean = false;
  public displaySSO: boolean = false; // TO-DO get from company settings

  public module: string = '';
  public user: string = '';
  public pass: string = '';
  prevUrl: string = '';

  constructor(
    private authorisationService: AuthorisationService,
    private mfaService: MfaService,
    private dialogService: DialogService,
    private loginNavigationService: LoginNavigationService,
    public translate: TranslateService,
    public sessionService: SessionService,
    public usersService: UsersService,
    public permissionService: PermissionService,
    public router: Router,
    public route: ActivatedRoute) { }

  ngOnInit(): void {
    this.init();
  }

  async init(): Promise<void> {
    this.prevUrl = this.route.snapshot.queryParams['previousUrl'];
    this.sessionService.menu = this.prevUrl;

    await this.checkLoginAlreadyDone();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onSsoLoginClick(): void {
    console.log('SSO triggered');
  }

  private async navigateAfterLogin() {
    const path = await this.loginNavigationService.resolveLandingPage();
    await this.loginNavigationService.navigateLandingPage(path);
  }

  async checkLoginAlreadyDone() {
    const isAuthenticated = await this.sessionService.isLogged();

    if (!isAuthenticated) {
      return;
    }
    await this.navigateAfterLogin();
  }

  async login(form?: NgForm): Promise<void> {

    const username = this.user?.trim();
    const password = this.pass?.trim();

    if (form?.invalid || !username || !password) {
      form?.control.markAllAsTouched();
      return;
    }

    try {
      this.sessionService.setLoading(true);
      this.sessionService.clearSession();

      const user = await this.sessionService.login(username.toLowerCase(), password);

      if (user?.challengeName === 'NEW_PASSWORD_REQUIRED') {
        await this.onShowChangePassword(user);
        return;
      } else if (user?.challengeName === 'SOFTWARE_TOKEN_MFA') {
        this.router.navigate(['/otp/verified']);
      } else if (user?.challengeName === 'MFA_ENROLMENT') {
        console.warn('MFA_ENROLMENT!');
      } else if (user?.challengeName === null) {
        console.warn("ChallengeName is null.");
      }

      if (this.sessionService.hasAccess) {
        await this.navigateAfterLogin();
      }

    } catch (error: any) {
      console.error("Unexpected error during sign-in:", error);
    } finally {
      this.sessionService.setLoading(false);
    }
  }

  async onShowChangePassword(user: any) : Promise<void> {
    const changePasswordFormRef = this.dialogService.open(
      ChangePasswordFormComponent,
      {
        data: {
          user,
          password: '',
          confirmPassword: ''
        },
        header: this.translate.instant('LOGIN.SET_PASSWORD'),
        width: '50%',
        contentStyle: { overflow: 'visible' }
      }
    );

    if (!changePasswordFormRef) {
      return;
    }

    changePasswordFormRef.onClose.subscribe(async (result: any) => {
      if (result?.action === true && result.data.password) {
        try {
          const user = result.data.user;

          const payload: NewPasswordChallengeRequest = {
            username: user.userName,
            password: result.data.password,
            session: user.session
          };

          const response = await firstValueFrom(
            this.authorisationService.apiAuthorisationNewPasswordChallengePost(payload)
          );

          if (response.challengeName === 'MFA_ENROLMENT') {
            const associatePayload: AssociateTokenRequest = {
              token: response.authenticationResult?.accessToken
            };

            const mfaAssociate = await firstValueFrom(this.mfaService.apiMfaAssociatePost(associatePayload));

            if (mfaAssociate?.url) {
              // qr Content & confif Key
              const qrData = encodeURIComponent(mfaAssociate.url);
              sessionStorage.setItem('mfaToken', associatePayload.token);
              sessionStorage.setItem('mfaQrUrl', mfaAssociate.url);

              if (mfaAssociate?.mfaConfigurationKey) {
                sessionStorage.setItem('mfaConfigKey', mfaAssociate.mfaConfigurationKey);
              }

              this.router.navigate(['/otp/setup'], { queryParams: { qrData } });
            } else {
              console.error('MFA enrolment failed: No QR URL received.');
            }

            return;
          }

          // Success login after pass change (tokens returned)
          this.sessionService.applyTokens(response.authenticationResult);
          await this.sessionService.getUser();

          this.sessionService.menu = this.prevUrl;
          if(this.prevUrl) {
          this.router.navigateByUrl(this.prevUrl);
          }
          else {
            await this.navigateAfterLogin();
          }

        } catch (error) {
          console.error("Error during password change and post-login:", error);
        }
      }
    });
  }
}
